import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useOpenSolarSync } from '@/hooks/use-opensolar-sync';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, Search, ArrowRight, ChevronRight } from 'lucide-react';

export default function OpenSolarPage() {
  const { toast } = useToast();
  const { projects, isLoadingProjects, triggerSync, isSyncing, syncStats } = useOpenSolarSync();
  const [searchTerm, setSearchTerm] = useState('');

  // Define project interface
  interface Project {
    id: string | number;
    name?: string;
    customerName?: string;
    address?: string;
    status?: string;
    updatedAt?: string;
  }

  // Filter projects based on search term
  const filteredProjects = projects.filter((project: Project) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      project.name?.toLowerCase().includes(term) ||
      false ||
      project.customerName?.toLowerCase().includes(term) ||
      false ||
      project.address?.toLowerCase().includes(term) ||
      false
    );
  });

  // Handle sync button click
  const handleSync = () => {
    triggerSync()
      .then(() => {
        toast({
          title: 'Sync Started',
          description: 'Synchronizing with OpenSolar...',
        });
      })
      .catch((error) => {
        toast({
          title: 'Sync Failed',
          description: error.message || 'An error occurred during synchronization',
          variant: 'destructive',
        });
      });
  };

  return (
    <div className="container py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">OpenSolar Integration</h1>
          <p className="text-muted-foreground mt-1">
            Manage and synchronize your OpenSolar projects
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={handleSync} disabled={isSyncing} className="flex items-center gap-2">
            <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync with OpenSolar'}
          </Button>

          <Link href="/dashboard">
            <Button variant="outline">Back to Dashboard</Button>
          </Link>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>OpenSolar Projects</CardTitle>
          <CardDescription>
            View and manage projects synchronized from the OpenSolar platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative w-full max-w-md mb-4">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search projects..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="overflow-hidden rounded-md border">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50">
                  <th className="p-3 text-left font-medium">Project ID</th>
                  <th className="p-3 text-left font-medium">Name</th>
                  <th className="p-3 text-left font-medium">Status</th>
                  <th className="p-3 text-left font-medium">Last Synced</th>
                  <th className="p-3 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingProjects ? (
                  <tr>
                    <td colSpan={5} className="p-4 text-center">
                      <div className="flex justify-center items-center space-x-2">
                        <RefreshCw className="h-5 w-5 animate-spin" />
                        <span>Loading projects...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-4 text-center text-muted-foreground">
                      {searchTerm
                        ? 'No projects match your search criteria'
                        : "No projects available. Click 'Sync with OpenSolar' to import your projects."}
                    </td>
                  </tr>
                ) : (
                  filteredProjects.slice(0, 10).map((project: Project) => (
                    <tr key={project.id} className="border-t hover:bg-muted/50">
                      <td className="p-3">{project.id}</td>
                      <td className="p-3 font-medium">{project.name}</td>
                      <td className="p-3">{project.status || 'Active'}</td>
                      <td className="p-3">
                        {project.updatedAt
                          ? new Date(project.updatedAt).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="p-3">
                        <Link href={`/opensolar/projects?id=${project.id}`}>
                          <Button variant="ghost" size="sm" className="flex items-center gap-1">
                            View <ChevronRight className="h-4 w-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <div className="text-sm text-muted-foreground">
            {!isLoadingProjects &&
              `Showing ${Math.min(filteredProjects.length, 5)} of ${filteredProjects.length} projects`}
          </div>
          <Link href="/opensolar/projects">
            <Button className="flex items-center gap-1">
              View All Projects <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}
